import { useMemo, useEffect, useState, useCallback, useRef } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { Button, Chip, Slider, Switch } from "@heroui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faArrowLeft,
    faTrash,
    faFloppyDisk,
    faPlus,
    faWandMagicSparkles,
    faMap,
} from "@fortawesome/free-solid-svg-icons";
import { Rx } from "@/types/rx";
import { BlockPlotFeature, SlicePlotFeature } from "@/types/plot";
import { RxType } from "@/types/enums";
import { GeoJSON, Feature } from "geojson";
import { Skeleton } from "@heroui/react";
import useRequest from "@/lib/request/useRequest";
import useMap from "@/lib/map/useMap";
import {
    addUpdateGeojsonSource,
    addUpdateGeojsonLayers,
    removeLayersIfExists,
    removeSourcesIfExists,
    updateLayerOpacity,
    updateLayerVisibility,
    moveLayersToTop,
} from "@/lib/utils/map";
import {
    PLOT_RX_SOURCE,
    PLOT_RX_FILL_LAYER,
    PLOT_RX_LINE_LAYER,
    PLOT_RX_PLOT_LABEL,
    PLOT_HIGHLIGHT_FILL_LAYER,
    PLOT_HIGHLIGHT_LINE_LAYER,
    AB_LINE_LAYER,
    AB_POINTS_LAYER,
    PLOT_GRID_LAYER,
} from "@/lib/map/layers";
import BlockPlotForm from "./BlockPlotForm";
import { BlockForm } from "./BlockPlotForm";
import SlicePlotForm from "./SlicePlotForm";
import { SliceForm } from "./SlicePlotForm";
import { useTranslation } from "react-i18next";

function PlotForm({
    fieldId,
    rxId,
    placement,
    pairId,
}: {
    fieldId: string | number | undefined;
    rxId: string | number | undefined;
    placement: "manual" | "automatic";
    pairId?: string | number;
}) {
    const { t } = useTranslation();
    const { useGetRequest, useMutateRequest } = useRequest();
    const navigate = useNavigate();
    const getRx = useGetRequest<Rx>({
        url: `/prescriptions/${rxId}/`,
        enabled: !!rxId,
    });
    const plotsPairId = useMemo(() => {
        if (pairId) return pairId;
        if (getRx.data) {
            const pairs = getRx.data.plots?.features?.map((plot) => {
                return plot.properties?.pair_id;
            });
            if (pairs?.length) {
                return Math.max(...pairs) + 1;
            } else {
                return 1;
            }
        }
        return undefined;
    }, [getRx.data, pairId]);
    const { map, mapLoaded } = useMap();

    const plotPair: Feature[] | undefined = useMemo(() => {
        if (!getRx.data || !plotsPairId) {
            return;
        }
        return getRx.data.plots?.features?.filter((plot) => {
            return plot.properties?.pair_id === Number(plotsPairId);
        });
    }, [getRx.data, plotsPairId]);

    const mapFeatures: GeoJSON = useMemo(() => {
        return {
            type: "FeatureCollection",
            features: !getRx.data?.table?.features
                ? []
                : getRx.data.table.features.concat(
                      getRx.data.plots?.features
                          .filter((plot) => {
                              return (
                                  plot.properties?.pair_id !==
                                  Number(plotsPairId)
                              );
                          })
                          .map((plot) => {
                              return {
                                  ...plot,
                                  properties: {
                                      ...plot.properties,
                                      target_rate_n:
                                          plot.properties?.n_rate ?? 0,
                                  },
                              };
                          }) || [],
                  ),
        };
    }, [getRx.data, plotsPairId]);

    useEffect(() => {
        if (!map || !mapLoaded || getRx.isLoading) return;
        addUpdateGeojsonSource(map, PLOT_RX_SOURCE, {
            type: "geojson",
            data: mapFeatures,
            tolerance: 0,
        });
        addUpdateGeojsonLayers(map, [
            PLOT_RX_FILL_LAYER,
            PLOT_RX_LINE_LAYER,
            PLOT_RX_PLOT_LABEL,
        ]);
        updateLayerVisibility(
            map,
            [
                PLOT_RX_FILL_LAYER.id,
                PLOT_RX_LINE_LAYER.id,
                PLOT_RX_PLOT_LABEL.id,
            ],
            true,
        );
        moveLayersToTop(map, [
            PLOT_RX_FILL_LAYER.id,
            PLOT_RX_LINE_LAYER.id,
            PLOT_RX_PLOT_LABEL.id,
            PLOT_HIGHLIGHT_FILL_LAYER.id,
            PLOT_HIGHLIGHT_LINE_LAYER.id,
        ]);
        const rates = mapFeatures.features.map((feature: Feature) => {
            const rate = feature.properties?.target_rate_n;
            return rate !== undefined ? rate : 0;
        });
        let min = Math.floor(Math.min(...rates));
        const max = Math.ceil(Math.max(...rates));
        if (max === min) {
            min = 0;
        }
        const mid = (min + max) / 2;
        map.setPaintProperty(PLOT_RX_FILL_LAYER.id, "fill-color", [
            "interpolate",
            ["linear"],
            ["get", "target_rate_n"],
            min,
            "#ff0000",
            mid,
            "#ffff00",
            max,
            "#00ff00",
        ]);
    }, [map, mapLoaded, mapFeatures, getRx.isLoading]);
    useEffect(() => {
        return () => {
            if (map) {
                removeLayersIfExists(map, [
                    PLOT_RX_FILL_LAYER.id,
                    PLOT_RX_LINE_LAYER.id,
                    PLOT_RX_PLOT_LABEL.id,
                ]);
                removeSourcesIfExists(map, [PLOT_RX_SOURCE]);
            }
        };
    }, [map]);

    const [mapOpacity, setMapOpacity] = useState<number | number[]>(0.5);
    useEffect(() => {
        if (!map) return;
        updateLayerOpacity(
            map,
            [PLOT_RX_FILL_LAYER.id, PLOT_HIGHLIGHT_FILL_LAYER.id],
            mapOpacity as number,
        );
    }, [map, mapOpacity]);

    const backToRx = () => {
        navigate({
            to: `/dashboard/fields/${fieldId}/rx/${rxId}/`,
        });
    };
    const [formData, setFormData] = useState<
        BlockForm | BlockPlotFeature[] | SliceForm | SlicePlotFeature[] | null
    >(null);
    const handleFormChange = useCallback(
        (
            data:
                | BlockForm
                | BlockPlotFeature[]
                | SliceForm
                | SlicePlotFeature[],
        ) => {
            setFormData(data);
        },
        [],
    );
    const createPlots = useMutateRequest({
        url: `/plots/`,
        method: "post",
        successMessage: t("success.plots_were_created", { ns: "messages" }),
        errorMessage: t("error.failed_to_create_plots", { ns: "messages" }),
    });
    const updatePlots = useMutateRequest({
        url: `/plots/update/`,
        method: "post",
        successMessage: t("success.plots_were_saved", { ns: "messages" }),
        errorMessage: t("error.failed_to_save_plots", { ns: "messages" }),
        onSuccess: backToRx,
    });
    const autoPlacePlots = useMutateRequest({
        url: `/prescriptions/${rxId}/place_plots/`,
        method: "put",
        successMessage: t("success.plots_were_auto_placed", { ns: "messages" }),
        errorMessage: t("error.failed_to_auto_place_plots", { ns: "messages" }),
        onSuccess: backToRx,
    });
    const deletePlots = useMutateRequest({
        url: `/plots/delete/`,
        method: "post",
        successMessage: t("success.plots_were_deleted", { ns: "messages" }),
        errorMessage: t("error.failed_to_delete_plots", { ns: "messages" }),
        onSuccess: backToRx,
    });

    const packagedFormData = useMemo(() => {
        if (!Array.isArray(formData) || !formData?.length) return;
        return formData.map((plot: BlockPlotFeature | SlicePlotFeature) => {
            return {
                ...plot,
                geometry: {
                    ...plot.geometry,
                },
                properties: {
                    ...plot.properties,
                    pair_id: plotsPairId,
                    field: Number(fieldId),
                    prescription: Number(rxId),
                    irrigation_system: getRx.data?.irrigation_system,
                },
            };
        });
    }, [formData, plotsPairId, fieldId, rxId, getRx.data?.irrigation_system]);

    const createdPlotPairId = useRef<string | number | undefined>(undefined);
    const createPlotPair = useCallback(
        (onSuccess: () => void) => {
            if (
                !packagedFormData ||
                !plotsPairId ||
                createdPlotPairId.current === plotsPairId
            )
                return;

            createdPlotPairId.current = plotsPairId;
            createPlots.mutate(
                {
                    data: packagedFormData,
                },
                {
                    onSuccess: () => {
                        onSuccess();
                    },
                    onError: () => {
                        createdPlotPairId.current = undefined;
                    },
                },
            );
        },
        [createPlots, packagedFormData, plotsPairId],
    );

    return (
        <div className="flex flex-col w-full h-full gap-2 px-2 mt-3 text-sm">
            {getRx.isLoading || !getRx.data?.rx_type ? (
                <Skeleton className="h-full rounded-lg" />
            ) : (
                <>
                    <div className="flex flex-row justify-between items-center w-full">
                        <Button
                            size="sm"
                            variant="light"
                            as={Link}
                            to={`/dashboard/fields/$fieldId/rx/${rxId}/`}
                        >
                            <FontAwesomeIcon icon={faArrowLeft} />
                            {t("back_to_rx", { ns: "rx" })}
                        </Button>
                        {!!plotsPairId && placement !== "automatic" && (
                            <Chip
                                color="primary"
                                variant="flat"
                                className="h-full"
                            >
                                {t("pair_id", { ns: "rx" })}: {plotsPairId}
                            </Chip>
                        )}
                    </div>
                    {getRx.data?.rx_type === RxType.ZONE ? (
                        <BlockPlotForm
                            fieldId={getRx.data.field}
                            placement={placement}
                            plots={plotPair}
                            onFormChange={handleFormChange}
                        />
                    ) : (
                        <SlicePlotForm
                            irrigationSystemId={getRx.data.irrigation_system}
                            placement={placement}
                            plots={plotPair}
                            rxGeometry={getRx.data.table}
                            onFormChange={handleFormChange}
                        />
                    )}
                    <div className="flex flex-row items-center w-full gap-1 p-2 text-primary">
                        <div className="flex flex-col w-1/4 text-sm">
                            <Slider
                                startContent={<FontAwesomeIcon icon={faMap} />}
                                size="sm"
                                hideValue={true}
                                minValue={0}
                                maxValue={1}
                                step={0.01}
                                value={mapOpacity}
                                onChange={setMapOpacity}
                            />
                            {((formData &&
                                !Array.isArray(formData) &&
                                "ab_line" in formData &&
                                formData?.ab_line) ||
                                (Array.isArray(formData) &&
                                    formData?.length > 0 &&
                                    "ab_line" in formData[0].properties &&
                                    formData[0]?.properties?.ab_line)) && (
                                <Switch
                                    size="sm"
                                    defaultSelected={true}
                                    onChange={(e) => {
                                        if (!map) return;
                                        const isChecked = e.target.checked;
                                        updateLayerVisibility(
                                            map,
                                            [
                                                AB_LINE_LAYER.id,
                                                AB_POINTS_LAYER.id,
                                                PLOT_GRID_LAYER.id,
                                            ],
                                            isChecked,
                                        );
                                    }}
                                >
                                    {t("map_ab_line", { ns: "rx" })}
                                </Switch>
                            )}
                        </div>
                        <p className="text-gray-500 text-right">
                            {t("use_the_form_to_adjust_plot_dimensions", {
                                ns: "rx",
                            })}
                            {placement === "manual"
                                ? t("use_the_map_to_place_them", { ns: "rx" })
                                : ""}
                        </p>
                    </div>
                    <div className="flex flex-row w-full gap-2 justify-end items-end px-2 mt-2">
                        <>
                            {!plotPair?.length && placement === "manual" && (
                                <Button
                                    fullWidth
                                    color="secondary"
                                    isLoading={
                                        createPlots.isPending ||
                                        getRx.isFetching
                                    }
                                    onPress={() => {
                                        if (!formData || !!plotPair?.length)
                                            return;
                                        createPlotPair(() => {
                                            getRx.refetch();
                                            navigate({
                                                to: `/dashboard/fields/${fieldId}/rx/${rxId}/plots/draw/`,
                                            });
                                        });
                                    }}
                                >
                                    <FontAwesomeIcon icon={faPlus} />
                                    {t("save_and_draw_more", { ns: "rx" })}
                                </Button>
                            )}
                            {!!plotPair?.length && placement === "manual" && (
                                <Button
                                    fullWidth
                                    color="danger"
                                    isLoading={deletePlots.isPending}
                                    onPress={() => {
                                        if (!plotPair?.length || !formData)
                                            return;
                                        deletePlots.mutate({
                                            data: plotPair.map((plot) => {
                                                return plot.id;
                                            }),
                                        });
                                    }}
                                >
                                    <FontAwesomeIcon icon={faTrash} />
                                    {t("delete_plots", { ns: "rx" })}
                                </Button>
                            )}
                            {placement === "automatic" && (
                                <Button
                                    fullWidth
                                    color="secondary"
                                    isLoading={autoPlacePlots.isPending}
                                    onPress={() => {
                                        if (!!plotPair?.length || !formData)
                                            return;
                                        autoPlacePlots.mutate({
                                            data: formData,
                                        });
                                    }}
                                >
                                    <FontAwesomeIcon
                                        icon={faWandMagicSparkles}
                                    />
                                    {t("auto_place_plots", { ns: "rx" })}
                                </Button>
                            )}
                            {placement === "manual" && (
                                <Button
                                    fullWidth
                                    color="secondary"
                                    isLoading={
                                        updatePlots.isPending ||
                                        createPlots.isPending ||
                                        getRx.isFetching
                                    }
                                    onPress={() => {
                                        if (!formData) return;
                                        if (!plotPair?.length) {
                                            createPlotPair(backToRx);
                                        } else {
                                            updatePlots.mutate({
                                                data: packagedFormData,
                                            });
                                        }
                                    }}
                                >
                                    <FontAwesomeIcon icon={faFloppyDisk} />
                                    {t("save_plots", { ns: "rx" })}
                                </Button>
                            )}
                        </>
                    </div>
                </>
            )}
        </div>
    );
}

export default PlotForm;
